import styled, { css, keyframes } from 'styled-components';
import { tabletMinWidth, desktopMinWidth } from '../../mixins';

const isArabic = navigator.language.startsWith('ar');
const textAlign = isArabic ? 'right' : 'left';

const spinning = keyframes`
    to {
        transform: rotate(360deg);
    }
`;

export const SuccessWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: auto;
  padding: 20px 16px;
  max-width: 432px;
  margin: 0 auto;
  text-align: ${textAlign};

  @media (min-width: ${desktopMinWidth}px) {
    justify-content: center;
  }
`;

export const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 11px 0;
  position: relative;
  width: 100%;
  max-width: 100%;
  background-color: #fff;
  z-index: 99;

  &:after {
    content: '';
    display: block;
    width: 100vw;
    height: 1px;
    background: #d9ebf1;
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
  }
`;

export const Logo = styled.div`
  width: 24px;
  height: 24px;
  background: url(${'../assets/logo.svg'}) center / contain no-repeat;
  margin-right: 7px;
`;

export const Text = styled.div`
  font-weight: 900;
  font-size: 18px;
  line-height: 19px;
`;

export const TitleWrapper = styled.div`
  padding: 0 0 16px;
  border-bottom: 1px solid #E9E9E9;
  position: relative;
  text-align: center;
`;

export const Title = styled.div`
  font-weight: 700;
  font-size: 28px;
  line-height: 36px;
  color: #323232;
`;

export const Subtitle = styled.div`
  font-size: 16px;
  line-height: 24px;
  margin-top: 6px;
  color: #323232;
`;

export const InstructionsTitle = styled.div`
  font-size: 14px;
  line-height: 18px;
  margin-top: 20px;
  color: #323232;
`;

export const InstructionsWrapper = styled.div`
  --counter-size: 48px;
  --pb: 20px;
  padding-bottom: var(--pb);
  margin-top: 20px;
  border-bottom: 1px solid #E9E9E9;
  counter-reset: my-sec-counter;
  position: relative;
  isolation: isolate;

  &:before {
    content: '';
    display: block;
    position: absolute;
    top: calc(var(--counter-size));
    left: ${isArabic ? 'auto' : 'calc(var(--counter-size) / 2)'};
		right: ${isArabic ? 'calc(var(--counter-size) / 2)' : 'auto'};
    width: 2px;
    height: calc(100% - var(--counter-size)*2 - var(--pb));
    background: repeating-linear-gradient(
      to bottom,
      #F2994A,
      #F2994A 4px,
      #ffffff 4px,
      #ffffff 8px
    );
    z-index: -1;
  }
`;

export const ItemRow = styled.div`
  width: 100%;
  min-height: var(--counter-size);
  font-weight: 500;
  font-size: 16px;
  line-height: 21px;
  padding-left: ${isArabic ? '0' : '64px'};
	padding-right: ${isArabic ? '64px' : '0'};
  position: relative;
  text-align: ${textAlign};
  color: #323232;
  display: flex;
  align-items: center;

  &:not(:first-child) {
    margin-top: 20px;
  }

  &:last-child {
		display: flex;
		justify-content: ${isArabic ? 'flex-end' : 'flex-start'};
		align-items: center;
	}

  span {
    font-weight: 700;
  }

  &:before {
    counter-increment: my-sec-counter;
    content: counter(my-sec-counter);
    display: flex;
    justify-content: center;
    align-items: center;
    width: var(--counter-size);
    height: var(--counter-size);
    padding: 4px;
    border-radius: 8px;
    background-color: rgba(254, 243, 235, 1);
    border: 1px solid rgba(252, 180, 117, 1);
    box-shadow: 0 0 4px 4px #fff;
    font-weight: 600;
    font-size: 24px;
    line-height: 48px;
    text-align: center;
    color: rgba(242, 153, 74, 1);
    position: absolute;
    top: 0;
    ${isArabic ? 'right' : 'left'}: 0;
    box-sizing: border-box;
  }
`;

export const Screenshot = styled.div`
  width: 100%;
  border: 1px solid rgba(251, 162, 83, 1);
  border-radius: 6px;
  margin-top: 8px;
  padding: 14px;
`;

export const ScreenshotButton = styled.div`
  width: min(195px, 70%);
  height: 33px;
  border-radius: 5px;
  background: rgba(254, 100, 54, 1);
  margin: 0 auto;
  font-weight: 700;
  font-size: 10px;
  line-height: 33px;
  text-align: center;
  color: #FFFFFF;
  box-sizing: border-box;
`;

export const ScreenshotHighlight = styled(ScreenshotButton)`
  margin-top: 10px;
  background: rgba(50, 232, 90, 0.1);
  border: 2px solid rgba(50, 232, 90, 1);
  font-size: 8px;
  color: rgba(254, 100, 54, 1);
  position: relative;

  &:after {
    content: '';
    display: block;
    width: 18px;
    height: 30px;
    background: url(${'../assets/arrow.png'}) center / contain no-repeat;
    position: absolute;
    top: 0;
    right: 0;
    transform: translate(150%, -50%);
  }
`;

export const CheersTitle = styled.div`
  font-weight: 700;
  font-size: 18px;
  line-height: 22px;
  margin-top: 22px;
  color: #323232;
  text-align: center;
`;

export const CheersText = styled.div`
  font-size: 14px;
  line-height: 18px;
  margin: 6px 0 32px;
  color: #323232;
  text-align: center;

  @media (min-width: ${tabletMinWidth}px) {
    margin-bottom: 24px;
  }
`;

export const DesktopQR = styled.div`
  display: none;

  @media (min-width: 450px) {
    background-color: rgba(254, 243, 235, 1);
    border: 1px solid rgba(242, 153, 74, 1);
    padding: 12px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    text-align: ${textAlign};
    flex-direction: ${isArabic ? 'row-reverse' : 'row'};
    width: 100%;
  }
  span {
			font-size: 16px;
			line-height: 21px;
			color: #323232;
		}
`;

export const QRCode = styled.div`
  ${({ $img, $open }) => css`
    min-width: 100px;
    height: 100px;
    border-radius: 6px;
		margin-right: ${isArabic ? '0' : '24px'};
		margin-left: ${isArabic ? '24px' : '0'};
    position: relative;
    background-color: #fff;
    background-image: url(${$img});
    background-size: 100px 100px;
    background-position: center;
    background-repeat: no-repeat;
  
    ${!$img && css`
      &:after {
        --loader-size: 48px;
        content: '';
        display: block;
        width: var(--loader-size);
        height: var(--loader-size);
        background-image: url(${'../assets/spinner-image.svg'});
        animation: ${spinning} 1.5s linear infinite;
        background-size: 100%;
        background-repeat: no-repeat;
        background-position: center center;
        position: absolute;
        top: calc(50% - (var(--loader-size) / 2));
        left: calc(50% - (var(--loader-size) / 2));
      }
    `}
    ${$open &&
		css`
			position: fixed;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			border-radius: 0;
			display: flex;
			justify-content: center;
			align-items: center;
			z-index: 100;
			background-size: auto min(314px, 100%);

			&:before,
			&:after {
				content: '';
				position: absolute;
				width: 20px;
				height: 2px;
				background: #000;
				top: 30px;
				right: 30px;
				transform: translate(-50%, -50%) rotate(45deg);
			}

			&:after {
				transform: translate(-50%, -50%) rotate(-45deg);
			}
		`};

  `}
`;
